import type { RestoreAccount } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { createCombineCondition } from '@/types/advanceFilter';

import { getByovPolicyJson } from './byovPolicyUtils';

import { createCloudProviderCondition } from '../cloudProviders/createCloudProviderCondition';
import { convertQuery } from '../cost-explorer/utils/queryUtils';
import { Loader } from '../layout/loading';
import { CopyableCode } from '../shared/copyableCode';

export const VaultOwnBucketCopyableAllAccountsPolicy = ({
  bucketName,
}: {
  bucketName: string;
}) => {
  const dal = useDAL();
  const { data: restoreAccountsBody, isLoading: restoreAccountsLoading } =
    dal.cloudAccounts.restore.useList(undefined, undefined, {
      filters: convertQuery(
        createCombineCondition([createCloudProviderCondition([Provider.Aws])])
      ),
    });

  if (restoreAccountsLoading) {
    return <Loader />;
  }

  return (
    <VaultOwnBucketCopyablePolicy
      bucketName={bucketName}
      restoreAccounts={restoreAccountsBody?.accounts ?? []}
    />
  );
};

export const VaultOwnBucketCopyablePolicy = ({
  bucketName,
  restoreAccounts,
}: {
  bucketName: string;
  restoreAccounts: RestoreAccount[];
}) => {
  const dal = useDAL();
  const { dlsgFrontend } = useFeatureFlags();
  const { data: scanningAccounts, isLoading: isLoadingScanningAccounts } =
    dal.cloudAccounts.scanning.useList();

  if (isLoadingScanningAccounts) {
    return <Loader />;
  }

  const scanningAccountId =
    scanningAccounts?.accounts.find(
      (account) => account.cloudProvider === Provider.Aws
    )?.providerAccountId || '';

  return (
    <CopyableCode
      title='policy.json'
      code={getByovPolicyJson(
        bucketName,
        scanningAccountId,
        dlsgFrontend,
        restoreAccounts
      )}
      maxHeight='320px'
      blur={!bucketName}
    />
  );
};
